import { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

// Cache and throttling constants
const AUTH_CACHE_KEY = "bgdatalinks_auth_cache";
const AUTH_CACHE_EXPIRY_KEY = "bgdatalinks_auth_cache_expiry";
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes in milliseconds

// Check if we're running in the web client environment
const isWebClient =
  window.location.href.includes("localhost:3000") ||
  !window.location.href.includes("localhost");

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [authCheckAttempted, setAuthCheckAttempted] = useState(false);
  const [lastAuthCheckTime, setLastAuthCheckTime] = useState(0);

  // Check authentication status on initial load
  useEffect(() => {
    const verifyAuth = async () => {
      // For web client, don't attempt authentication - just initialize
      if (isWebClient) {
        // console.log(
        //   "Web client detected, skipping authentication verification"
        // );
        setIsInitialized(true);
        return;
      }

      // Prevent multiple verification attempts if one has already failed
      if (authCheckAttempted) {
        setIsInitialized(true);
        return;
      }

      // Implement throttling - don't verify auth more than once per 15 seconds
      const now = Date.now();
      if (now - lastAuthCheckTime < 15000) {
        setIsInitialized(true);
        return;
      }

      // Check for cached authentication data
      try {
        const cachedAuth = sessionStorage.getItem(AUTH_CACHE_KEY);
        const cacheExpiry = sessionStorage.getItem(AUTH_CACHE_EXPIRY_KEY);

        if (cachedAuth && cacheExpiry && now < parseInt(cacheExpiry)) {
          const userData = JSON.parse(cachedAuth);
          if (userData) {
            setUser(userData);
            setIsInitialized(true);
            return;
          }
        }
      } catch (err) {
        console.log("Error retrieving from auth cache:", err);
        // Continue with API call if cache retrieval fails
      }

      try {
        setLastAuthCheckTime(now);
        // Try to fetch current user - cookies will be used automatically
        const response = await api.get("/users/me");
        if (response.data.status === "success") {
          const userData = response.data.data.user;
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));

          // Cache the auth data
          try {
            sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
            sessionStorage.setItem(
              AUTH_CACHE_EXPIRY_KEY,
              (now + CACHE_TTL).toString()
            );
          } catch (err) {
            console.log("Error saving to auth cache:", err);
          }
        }
      } catch (error) {
        // Clear user data if unauthorized
        if (error.response?.status === 401) {
          localStorage.removeItem("user");
          sessionStorage.removeItem(AUTH_CACHE_KEY);
          sessionStorage.removeItem(AUTH_CACHE_EXPIRY_KEY);
          setUser(null);
        }

        // Don't set error for 429 status to avoid UI disruption
        if (error.response?.status !== 429) {
          setAuthError(error.message || "Authentication check failed");
        }
        console.error("Auth verification error:", error);
      } finally {
        setAuthCheckAttempted(true);
        setIsInitialized(true);
      }
    };

    verifyAuth();
  }, [authCheckAttempted, lastAuthCheckTime]);

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));

    // Update cache as well
    try {
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(userData));
      sessionStorage.setItem(
        AUTH_CACHE_EXPIRY_KEY,
        (Date.now() + CACHE_TTL).toString()
      );
    } catch (err) {
      console.log("Error updating auth cache:", err);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setAuthError(null);

    try {
      const response = await api.login(credentials);
      const {
        data: { user },
      } = response.data;

      // Store only user data in localStorage, not tokens
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);

      // Update auth cache
      try {
        sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
        sessionStorage.setItem(
          AUTH_CACHE_EXPIRY_KEY,
          (Date.now() + CACHE_TTL).toString()
        );
      } catch (err) {
        console.log("Error updating auth cache after login:", err);
      }

      // Reset auth check status to allow verification on next mount
      setAuthCheckAttempted(false);

      return response.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Login failed. Please try again.";
      setAuthError(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    setLoading(true);
    setAuthError(null);

    try {
      const response = await api.register(userData);
      const {
        data: { user },
      } = response.data;

      // Store only user data in localStorage, not tokens
      localStorage.setItem("user", JSON.stringify(user));
      setUser(user);

      // Update auth cache
      try {
        sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
        sessionStorage.setItem(
          AUTH_CACHE_EXPIRY_KEY,
          (Date.now() + CACHE_TTL).toString()
        );
      } catch (err) {
        console.log("Error updating auth cache after registration:", err);
      }

      // Reset auth check status to allow verification on next mount
      setAuthCheckAttempted(false);

      return response.data;
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      setAuthError(errorMsg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      // Clear local storage and session storage
      localStorage.removeItem("user");
      sessionStorage.removeItem(AUTH_CACHE_KEY);
      sessionStorage.removeItem(AUTH_CACHE_EXPIRY_KEY);
      setUser(null);
      setAuthCheckAttempted(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isInitialized,
        authError,
        login,
        register,
        logout,
        updateUser,
        retryAuthCheck: () => {
          setAuthCheckAttempted(false);
          setLastAuthCheckTime(0); // Reset time to force a check
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
